import {
    createContext,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmationDialog from '../components/Common/ConfirmationDialog'

interface UnsavedChangesContextValue {
    /**
     * Mark the current in-page form as having (or no longer having) unsaved edits.
     * Any form that opts in should call setDirty(true) on the first edit and
     * setDirty(false) on save, discard, and unmount.
     */
    setDirty: (dirty: boolean) => void
    /**
     * Run `action` immediately when nothing is dirty; otherwise show a confirm
     * popup and only run it if the user chooses to leave. Wrap every navigate-away
     * trigger (side-nav, org switch, logout, in-form cancel) in this.
     */
    guard: (action: () => void) => void
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(
    null,
)

export const useUnsavedChanges = (): UnsavedChangesContextValue => {
    const ctx = useContext(UnsavedChangesContext)
    if (!ctx) {
        throw new Error(
            'useUnsavedChanges must be used within an UnsavedChangesProvider',
        )
    }
    return ctx
}

interface UnsavedChangesProviderProps {
    children: React.ReactNode
}

/**
 * App-wide guard for in-page forms (Programs Add/Edit today; CCL/FM Settings can
 * adopt it later — see UBQ-5259). BrowserRouter has no router-level blocker
 * (useBlocker needs a data router), so every navigate-away surface calls guard()
 * and the single confirm popup lives here.
 *
 * The dirty flag is a ref, not state, so marking a form dirty never re-renders the
 * whole app and guard() always reads the latest value.
 */
export const UnsavedChangesProvider = ({
    children,
}: UnsavedChangesProviderProps): ReactElement => {
    const { t } = useTranslation()
    const isDirtyRef = useRef(false)
    const pendingActionRef = useRef<(() => void) | null>(null)
    const [showDialog, setShowDialog] = useState(false)

    const setDirty = useCallback((dirty: boolean) => {
        isDirtyRef.current = dirty
    }, [])

    const guard = useCallback((action: () => void) => {
        if (isDirtyRef.current) {
            pendingActionRef.current = action
            setShowDialog(true)
        } else {
            action()
        }
    }, [])

    const handleLeave = useCallback(() => {
        isDirtyRef.current = false
        setShowDialog(false)
        const action = pendingActionRef.current
        pendingActionRef.current = null
        action?.()
    }, [])

    const handleStay = useCallback(() => {
        pendingActionRef.current = null
        setShowDialog(false)
    }, [])

    /**
     * Native refresh / tab close / browser back — the only leave paths JS cannot
     * intercept with a custom dialog, so fall back to the browser's own prompt.
     */
    useEffect(() => {
        const handler = (event: BeforeUnloadEvent) => {
            if (isDirtyRef.current) {
                event.preventDefault()
                event.returnValue = ''
            }
        }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [])

    return (
        <UnsavedChangesContext.Provider value={{ setDirty, guard }}>
            {children}
            <ConfirmationDialog
                openDialog={showDialog}
                handleComfirm={handleLeave}
                handleClose={handleStay}
                DialogHeaderText={t('unsavedChangesTitle')}
                DialogBodyText={t('unsavedChangesMessage')}
                confirmtext={t('leave')}
                cancelText={t('stay')}
                confirmId="unsaved-changes-leave-button"
                cancelId="unsaved-changes-stay-button"
            />
        </UnsavedChangesContext.Provider>
    )
}
